import React, { useState, useEffect, useRef } from "react"
import { Modal, Spin, Alert } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { usePDF } from "@react-pdf/renderer"
import html2canvas from "html2canvas"
import PlanImplicationsPDF, { sliceCanvasAtCards } from "./PlanImplicationsPDF"
import "../../../assets/stylesheets/rubrics/implementation/plan.scss"
import riePlanning from "../../../assets/images/rie-plan.png"
import sendIcon from "../../../assets/images/send.png"
import VoiceInput from "../shared/VoiceInput"
import pdfSvg from "../../../assets/images/pdf-file.svg"

const sortByRating = (plan) => {
  const flat = (plan || []).flatMap((stage) => (stage.indicators || []).map((ind) => ({
    index: ind.index,
    label: ind.label,
    explanation: ind.explanation,
    rating: ind.rating,
  })))

  return flat.sort((a, b) => {
    if (a.rating === null && b.rating === null) return 0
    if (a.rating === null) return 1
    if (b.rating === null) return -1
    if (a.rating === 0 && b.rating === 0) return 0
    if (a.rating === 0) return 1
    if (b.rating === 0) return -1
    return b.rating - a.rating
  })
}

const PlanChatSection = ({ rubricId }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Rei 👋 Ask me anything about your current school plan — I'm here to help you reflect and take next steps.",
    },
  ])
  const [input, setInput] = useState("")
  const [chatLoading, setChatLoading] = useState(false)
  const [isVoiceRecording, setIsVoiceRecording] = useState(false)
  const messagesRef = useRef(null)
  const bottomRef = useRef(null)
  const isFirstRender = useRef(true)

  const handleVoiceTranscription = (text) => {
    setInput((prev) => (prev ? `${prev} ${text}` : text))
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return undefined
    }
    const timer = setTimeout(() => {
      if (messagesRef.current) {
        messagesRef.current.scrollTo({
          top: messagesRef.current.scrollHeight,
          behavior: "smooth",
        })
      }
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [messages])

  const sendMessage = () => {
    const text = input.trim()
    if (!text || chatLoading) return

    const userMsg = { role: "user", content: text }
    const history = messages
      .slice(1)
      .map((m) => ({ role: m.role, content: m.content }))
    setMessages((prev) => [...prev, userMsg])
    setInput("")
    setChatLoading(true)

    // eslint-disable-next-line no-undef
    APIClient.post(
      `/implementation_rubrics/${rubricId}/plan_chat`,
      { message: text, history },
      (response) => {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: response.reply },
        ])
        setChatLoading(false)
      },
      () => {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, I ran into an issue. Please try again.",
          },
        ])
        setChatLoading(false)
      },
    )
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="rie-chat">
      <div className="rie-chat-header">
        <span className="label">Ask Rei about your School Plan</span>
        <img src={riePlanning} className="rie-chat-header-icon" alt="Rei" />
      </div>

      <div className="rie-chat-messages" ref={messagesRef}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`rie-chat-message-row rie-chat-message-row--${msg.role}`}
          >
            {msg.role === "assistant" && (
              <div className="rie-chat-avatar">
                <img src={riePlanning} alt="Rei" />
              </div>
            )}
            <div className={`rie-chat-bubble rie-chat-bubble--${msg.role}`}>
              {msg.role === "assistant" && (
                <span className="rie-chat-sender">Rei</span>
              )}
              {msg.content}
            </div>
          </div>
        ))}

        {chatLoading && (
          <div className="rie-chat-typing">
            <div className="rie-chat-avatar">
              <img src={riePlanning} alt="Rei" />
            </div>
            <div className="loader-css" />
          </div>
        )}
      </div>

      <div className="rie-chat-input-row">
        <div className="rie-chat-input-wrapper">
          <input
            type="text"
            className="rie-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Rei anything about your school plan..."
            disabled={chatLoading}
          />
          <div className="rie-chat-input-icons">
            <VoiceInput
              onTranscriptionComplete={handleVoiceTranscription}
              onRecordingStateChange={(recording) => setIsVoiceRecording(recording)}
              disabled={chatLoading}
              disabledTooltipMessage="Voice Input Disabled During AI Response Generation"
            />
          </div>
        </div>
        <button
          className="rie-chat-send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || chatLoading || isVoiceRecording}
        >
          <img src={sendIcon} alt="Send" />
        </button>
      </div>
      <div ref={bottomRef} />
    </div>
  )
}

const PlanImplicationsSummaryModal = ({
  visible,
  onClose,
  rubricId,
  rubricPlan,
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [summaryPages, setSummaryPages] = useState([])
  const [suggestionPages, setSuggestionPages] = useState([])

  const canvasesReady = summaryPages.length > 0 && suggestionPages.length > 0
  const [pdfInstance, updatePdf] = usePDF({ document: undefined })

  useEffect(() => {
    if (canvasesReady) {
      updatePdf(
        <PlanImplicationsPDF
          summaryPages={summaryPages}
          suggestionPages={suggestionPages}
        />,
      )
    }
  }, [canvasesReady, summaryPages, suggestionPages])

  const pollRef = useRef(null)

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const startPolling = () => {
    stopPolling()
    pollRef.current = setInterval(() => {
      // eslint-disable-next-line no-undef
      APIClient.get(
        `/implementation_rubrics/${rubricId}/plan_implications_result`,
        (response) => {
          if (response.status === "completed") {
            stopPolling()
            setData(response.data)
            setLoading(false)
          } else if (response.status === "failed") {
            stopPolling()
            setError(
              (response.data && response.data.error)
                || "Failed to generate summary. Please try again.",
            )
            setLoading(false)
          }
          // "pending" / "processing" → keep polling
        },
      )
    }, 3000)
  }

  const fetchSummary = () => {
    setLoading(true)
    setError(null)
    setData(null)
    setCurrentPage(0)
    // eslint-disable-next-line no-undef
    APIClient.post(
      `/implementation_rubrics/${rubricId}/plan_implications_summary`,
      {},
      (response) => {
        if (response.status === "completed") {
          setData(response.data)
          setLoading(false)
        } else {
          startPolling()
        }
      },
      (err) => {
        setError(
          (err && err.error) || "Failed to generate summary. Please try again.",
        )
        setLoading(false)
      },
    )
  }

  useEffect(() => {
    if (visible) {
      fetchSummary()
    }
    return () => stopPolling()
  }, [visible])

  useEffect(() => {
    if (data) {
      setSummaryPages([])
      setSuggestionPages([])
      setTimeout(() => {
        const el1 = document.getElementById("pisPdfSummary")
        const el2 = document.getElementById("pisPdfSuggestions")
        if (el1) {
          html2canvas(el1, { scale: 2 }).then((canvas) => {
            // Summary is always one page — cap height so any bottom padding/border
            // overflow doesn't produce a blank second page.
            const pageH = Math.floor((canvas.width * 841.89) / 595.28)
            const capH = Math.min(canvas.height, pageH)
            const page = document.createElement("canvas")
            page.width = canvas.width
            page.height = pageH
            const ctx = page.getContext("2d")
            ctx.fillStyle = "#ffffff"
            ctx.fillRect(0, 0, page.width, page.height)
            ctx.drawImage(
              canvas,
              0,
              0,
              canvas.width,
              capH,
              0,
              0,
              canvas.width,
              capH,
            )
            setSummaryPages([page.toDataURL("image/png")])
          })
        }
        if (el2) {
          html2canvas(el2, { scale: 2 }).then((canvas) => {
            setSuggestionPages(
              sliceCanvasAtCards(canvas, el2, ".pis-pdf-card"),
            )
          })
        }
      }, 1000)
    }
  }, [data])

  const handleClose = () => {
    stopPolling()
    setData(null)
    setError(null)
    setLoading(false)
    setCurrentPage(0)
    setSummaryPages([])
    setSuggestionPages([])
    onClose()
  }

  const sortedPlan = sortByRating(rubricPlan)

  const sortedSuggestions = data
    ? sortedPlan
      .map((planInd) => ({
        planInd,
        suggestion: (data.indicator_suggestions || []).find(
          (s) => s.index === planInd.index,
        ),
      }))
      .filter(({ suggestion }) => suggestion)
    : []

  const isOverallPage = currentPage === 0
  const currentSuggestion = !isOverallPage
    ? sortedSuggestions[currentPage - 1]
    : null

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={660}
      height={600}
      centered
      closable={false}
      className="pis-modal-wrapper"
    >
      <div className="pis-modal-header">
        <div>
          <div className="pis-modal-title">Rei-Assisted Plan Analysis</div>
          <div className="pis-modal-subtitle">
            A focused review of proposed improvements and adjustments
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {data
            && !loading
            && (canvasesReady && !pdfInstance.loading && pdfInstance.url ? (
              <a
                href={pdfInstance.url}
                download="plan-implications-summary.pdf"
                style={{ textDecoration: "none" }}
              >
                <button
                  className="pis-modal-download-btn"
                  title="Download as PDF"
                >
                  <img
                    src={pdfSvg}
                    alt="Download PDF"
                    style={{ width: "40px" }}
                  />
                </button>
              </a>
            ) : (
              <button className="pis-modal-download-btn" disabled>
                <Spin size="small" />
                {/* <span>Preparing PDF…</span> */}
              </button>
            ))}
          <button className="pis-modal-close" onClick={handleClose}>
            <CloseOutlined />
          </button>
        </div>
      </div>
      {loading && (
        <div className="pis-modal-loading">
          <Spin size="large" />
          <p>Analyzing to determine implications of your plan...</p>
        </div>
      )}

      {error && !loading && <Alert type="error" message={error} showIcon />}

      {data && !loading && (
        <>
          <div className="pis-modal-content-box-main">
            <div className="pis-modal-content-box-main-inner">
              <div className="pis-modal-icon-wrapper">
                <img
                  src={riePlanning}
                  className="riePlanning-icon"
                  style={{
                    width: "171px",
                    height: "183px",
                  }}
                />
              </div>

              <div className="pis-modal-teal-label">
                <span>Plan Implications Summary</span>
              </div>
              <div className="pis-modal-inner-content">
                <div className="pis-modal-content-box">
                  <p className="pis-modal-content-text">
                    {isOverallPage
                      ? data.overall_summary
                      : currentSuggestion.planInd.explanation
                        || "No description provided."}
                  </p>
                </div>

                {!isOverallPage && currentSuggestion && (
                  <>
                    <div className="pis-modal-suggestion-label">
                      Rei&apos;s suggestion
                    </div>
                    <div className="pis-modal-cards">
                      {(currentSuggestion.suggestion.suggestions || []).map(
                        (s, idx) => (
                          <div key={idx} className="pis-modal-card">
                            <div className="pis-modal-card-number">
                              <span className="pis-modal-card-number-label">Indicator</span>
                              <span className="pis-modal-card-number-value">{idx + 1}</span>
                            </div>
                            <div className="pis-modal-card-text">{s}</div>
                          </div>
                        ),
                      )}
                    </div>
                  </>
                )}

                {isOverallPage && (
                  <>
                    <div className="pis-modal-suggestion-label">
                      Rei&apos;s Suggestions
                    </div>

                    <div className="pis-modal-cards">
                      <span className="pis-modal-card-color" />
                      {sortedSuggestions.map(({ planInd, suggestion }) => (
                        <div key={planInd.index} className="pis-modal-card">
                          <div className="pis-modal-card-number">
                            <span className="pis-modal-card-number-label">Indicator</span>
                            <span className="pis-modal-card-number-value">{planInd.index}</span>
                          </div>
                          <div className="pis-modal-card-text">
                            {(suggestion.suggestions || [])[0]}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <PlanChatSection rubricId={rubricId} />
            </div>
          </div>
        </>
      )}
      {data && !loading && (
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          {/* Page 1: Plan Implications Summary */}
          <div
            id="pisPdfSummary"
            className="pis-pdf-capture"
            style={{ width: 612, padding: "24px", background: "#fff" }}
          >
            <div className="pis-pdf-icon-wrapper">
              <img src={riePlanning} className="pis-pdf-rei-icon" />
            </div>
            <div className="pis-pdf-teal-label">
              <span>Plan Implications Summary</span>
            </div>
            <div className="pis-pdf-inner-content">
              <div className="pis-pdf-content-box">
                <p className="pis-pdf-content-text">{data.overall_summary}</p>
              </div>
            </div>
          </div>

          {/* Page 2+: Rei's Suggestions */}
          <div
            id="pisPdfSuggestions"
            className="pis-pdf-capture"
            style={{ width: 612, padding: "24px", background: "#fff" }}
          >
            <div className="pis-modal-suggestion-label">
              <span>Rei&apos;s Suggestions</span>
            </div>
            <div className="pis-pdf-inner-content">
              <div className="pis-pdf-cards">
                {sortedSuggestions.map(({ planInd, suggestion }) => (
                  <div key={planInd.index} className="pis-pdf-card">
                    <div className="pis-pdf-card-number">
                      <span className="pis-pdf-card-number-label">INDICATOR</span>
                      <span className="pis-pdf-card-number-value">{planInd.index}</span>
                    </div>
                    <div className="pis-pdf-card-text">{(suggestion.suggestions || [])[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default PlanImplicationsSummaryModal
